import React from 'react';
import { TrendingUpIcon } from 'lucide-react';
interface Scenario {
  id: number;
  name: string;
  revenueTarget: number;
  confidence: number;
}
interface ForecastVisualizationProps {
  scenario: Scenario;
}
export function ForecastVisualization({
  scenario
}: ForecastVisualizationProps) {
  const quarters = [{
    name: 'Q1',
    actual: 2100000,
    forecast: 2000000,
    confidence: 82
  }, {
    name: 'Q2',
    actual: 2450000,
    forecast: 2500000,
    confidence: 85
  }, {
    name: 'Q3',
    actual: null,
    forecast: 2800000,
    confidence: 78
  }, {
    name: 'Q4',
    actual: null,
    forecast: 2700000,
    confidence: 72
  }];
  const maxValue = Math.max(...quarters.map(q => q.forecast));
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1202">
      <div className="flex items-center justify-between mb-6" data-id="element-1203">
        <div className="flex items-center gap-2" data-id="element-1204">
          <TrendingUpIcon className="w-5 h-5 text-red-600" data-id="element-1205" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1206">
            Revenue Forecast with Confidence Scoring
          </h2>
        </div>
        <div className="flex items-center gap-4" data-id="element-1207">
          <div className="flex items-center gap-2" data-id="element-1208">
            <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-1209" />
            <span className="text-sm text-gray-600" data-id="element-1210">Actual</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1211">
            <div className="w-3 h-3 bg-purple-500 rounded-full" data-id="element-1212" />
            <span className="text-sm text-gray-600" data-id="element-1213">Forecast</span>
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="mb-6" data-id="element-1214">
        <div className="flex items-end justify-between gap-4 h-64" data-id="element-1215">
          {quarters.map((quarter, index) => <div key={index} className="flex-1 flex flex-col justify-end" data-id="element-1216">
              <div className="relative h-full flex items-end gap-2" data-id="element-1217">
                {/* Actual Bar */}
                {quarter.actual && <div className="flex-1 relative" data-id="element-1218">
                    <div className="bg-blue-500 rounded-t-lg transition-all hover:bg-blue-600" style={{
                height: `${quarter.actual / maxValue * 100}%`
              }} data-id="element-1219" />
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-900 whitespace-nowrap" data-id="element-1220">
                      {formatCurrency(quarter.actual)}
                    </div>
                  </div>}
                {/* Forecast Bar */}
                <div className="flex-1 relative" data-id="element-1221">
                  <div className="bg-purple-500 rounded-t-lg transition-all hover:bg-purple-600" style={{
                height: `${quarter.forecast / maxValue * 100}%`
              }} data-id="element-1222" />
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-900 whitespace-nowrap" data-id="element-1223">
                    {formatCurrency(quarter.forecast)}
                  </div>
                </div>
              </div>
              {/* Quarter Label & Confidence */}
              <div className="mt-3 text-center" data-id="element-1224">
                <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1225">
                  {quarter.name}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(quarter.confidence)}`} data-id="element-1226">
                  {quarter.confidence}% confidence
                </span>
              </div>
            </div>)}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200" data-id="element-1227">
        <div data-id="element-1228">
          <p className="text-xs text-gray-600 mb-1" data-id="element-1229">Total Forecast</p>
          <p className="text-lg font-bold text-gray-900" data-id="element-1230">
            {formatCurrency(quarters.reduce((sum, q) => sum + q.forecast, 0))}
          </p>
        </div>
        <div data-id="element-1231">
          <p className="text-xs text-gray-600 mb-1" data-id="element-1232">YTD Actual</p>
          <p className="text-lg font-bold text-gray-900" data-id="element-1233">
            {formatCurrency(quarters.reduce((sum, q) => sum + (q.actual || 0), 0))}
          </p>
        </div>
        <div data-id="element-1234">
          <p className="text-xs text-gray-600 mb-1" data-id="element-1235">Remaining</p>
          <p className="text-lg font-bold text-gray-900" data-id="element-1236">
            {formatCurrency(quarters.filter(q => !q.actual).reduce((sum, q) => sum + q.forecast, 0))}
          </p>
        </div>
        <div data-id="element-1237">
          <p className="text-xs text-gray-600 mb-1" data-id="element-1238">Avg Confidence</p>
          <p className="text-lg font-bold text-green-600" data-id="element-1239">
            {Math.round(quarters.reduce((sum, q) => sum + q.confidence, 0) / quarters.length)}
            %
          </p>
        </div>
      </div>
    </div>;
}